import {
  CommentConversion,
  Content,
  ContentNode,
  ContentNodeType,
  ContentSubType,
  ContentType,
  Epic,
  Task,
} from '@amzn/awscat-pattern-library-service-model';
import { getHTML, getJSON, getText } from '@amzn/awscat-editor';

import { autoGenerateHref, concatContentData } from '../api/ContentUtil';
import { deepCloneContent } from '../model/CloneContent';
import { ContentSchemaSection } from '../model/ContentSchemaSection';
import { ContentConversionSubTypeMap } from '../components/utils/SelectableOptions';
import { getSkillLabels } from '../components/utils/TaskUtils';
import {
  convertCodeRepository,
  convertContentMetadata,
  convertContentTypeMetadata,
  convertTemplateMetadata,
  getContentPages,
  getContentSections,
  getH2Heading,
  getH3Heading,
  getSeparator,
  getTemplateSchema,
} from '../components/utils/ConverterUtils';
import { ROOT_SECTION_ID } from '../model/Contents';
import { SpecialSectionId } from './SectionOperations';

/** Pages mapped to default sections when converting Guide → Pattern, matching legacy ContentConverterInput defaults. */
const ROOT_PAGE_DEFAULT_SECTION_ID = 'summary';
const NON_ROOT_PAGE_DEFAULT_SECTION_ID = 'miscellaneous';

/** Converted content + the inline-comment re-anchor requests the server applies after the body is saved. */
export interface ContentConversionResult {
  readonly content: Content;
  readonly commentConversions: readonly CommentConversion[];
}

/** @throws when the conversion pair (`fromType` → `targetType`) is not yet supported. */
export function convertContent(content: Content, targetType: ContentType): ContentConversionResult {
  const fromType = content.contentMetadata.contentType;
  if (fromType === ContentType.Pattern && targetType === ContentType.Guide) {
    return convertPatternToGuide(content);
  }
  if (fromType === ContentType.Guide && targetType === ContentType.Pattern) {
    return convertGuideToPattern(content);
  }
  throw new Error('Content type conversion is not yet supported in the new editor.');
}

/**
 * Pattern → Guide. Mirrors {@link SingleToMultiPageConverter}: existing
 * Pattern sections become Guide pages, the special `epics` and
 * `troubleshooting` sections are flattened into Guide pages with their
 * structured data converted to inline HTML headings.
 */
function convertPatternToGuide(content: Content): ContentConversionResult {
  const targetType = ContentType.Guide;
  const contentSubType = requireConversionSubType(targetType);
  const commentConversions: CommentConversion[] = [];

  // Deep-clone (with model prototypes restored) so the input Content
  // stays untouched and downstream code that round-trips through
  // `json2typescript` still sees `Content`/`ContentNode` instances.
  const updated = deepCloneContent(content);

  convertContentTypeMetadata(updated.contentTypeMetadata, contentSubType);
  convertContentMetadata(updated.contentMetadata, targetType);
  convertTemplateMetadata(updated, contentSubType);

  // Pattern sections become Guide pages. `summary` keeps its place under
  // `contentSections` on the root; the rest move to `subContent`.
  const sections = getContentSections(updated);
  const summarySection = sections.find((s) => s.id === ROOT_PAGE_DEFAULT_SECTION_ID);
  updated.contentData.contentSections = summarySection ? [summarySection] : [];
  const movableSections = sections.filter((s) => s.id !== ROOT_PAGE_DEFAULT_SECTION_ID);

  const structuredSectionIds = new Set<string>(Object.values(SpecialSectionId));
  for (const section of movableSections) {
    if (section.id === SpecialSectionId.Epics) {
      flattenEpicsSectionIntoPage(section, commentConversions);
    } else if (section.id === SpecialSectionId.Troubleshooting) {
      flattenTroubleshootingSectionIntoPage(section);
    } else if (structuredSectionIds.has(section.id)) {
      // Future structured sections: promote to page with empty body.
      section.nodeType = ContentNodeType.Page;
      section.contentData = '';
    } else {
      section.nodeType = ContentNodeType.Page;
    }
  }
  updated.contentData.subContent = movableSections;

  convertCodeRepository(updated, targetType);
  autoGenerateHref(updated.contentData, updated.contentMetadata, true);

  return { content: updated, commentConversions };
}

/** @throws when no conversion subtype is registered for `targetType`. */
function requireConversionSubType(targetType: ContentType): ContentSubType {
  const contentSubType = ContentConversionSubTypeMap.get(targetType);
  if (contentSubType === undefined) {
    throw new Error(`No conversion subtype registered for content type ${targetType}.`);
  }
  return contentSubType;
}

/**
 * Flattens an `epics` section's structured `Epic[]` into HTML on the page
 * body. Each epic becomes an `<H2>` block followed by its tasks as `<H3>`
 * blocks. Emits one {@link CommentConversion} per epic so server-side
 * comment paths anchored under `epics:<epicId>` are remapped to the new
 * page section id.
 */
function flattenEpicsSectionIntoPage(
  section: ContentNode,
  commentConversions: CommentConversion[],
): void {
  section.nodeType = ContentNodeType.Page;

  if (!Array.isArray(section.contentData) || section.contentData.length === 0) {
    section.contentData = '';
    return;
  }

  const epics = section.contentData as unknown as Epic[];
  section.contentData = '';
  for (const epic of epics) {
    const concatData = concatContentData(
      section.contentData,
      getH2Heading(getText(epic.title)),
      epic.description,
      '<br>',
    );
    section.contentData = getJSON(concatData);
    commentConversions.push(CommentConversion.create('epics:' + epic.id, section.id));
    for (const task of epic.tasks ?? []) appendTaskToPage(task, section);
  }
}

/** Appends one task to the current page body as `<H3>` + description + skills. */
function appendTaskToPage(task: Task, section: ContentNode): void {
  const parts: Array<string | object> = [
    section.contentData,
    getH3Heading(getText(task.title)),
    task.description,
  ];
  if (task.skills) parts.push(getSkillLabels(task.skills));
  section.contentData = getJSON(concatContentData(...parts));
}

/**
 * Flattens a `troubleshooting` section's `[{ issue, solution }]` array
 * into a single HTML block on the page. No CommentConversions are emitted
 * because legacy didn't track per-troubleshooting-row comment paths.
 */
function flattenTroubleshootingSectionIntoPage(section: ContentNode): void {
  section.nodeType = ContentNodeType.Page;

  // Mirror flattenEpicsSectionIntoPage: an empty section stays as `''` so
  // downstream serialization doesn't see `{}` (what `getJSON('')` returns).
  if (!Array.isArray(section.contentData) || section.contentData.length === 0) {
    section.contentData = '';
    return;
  }

  let convertedContentData = '';
  for (const { issue, solution } of section.contentData) {
    convertedContentData = concatContentData(convertedContentData, issue, '<br>', solution, '<hr>');
  }
  section.contentData = getJSON(convertedContentData);
}

/**
 * Guide → Pattern. Mirrors {@link MultiToSinglePageConverter}: every Guide
 * page is collapsed back into one of the Pattern's schema sections via
 * the legacy default mapping (`rootId → summary`, others → `miscellaneous`).
 * Emits {@link CommentConversion}s so the server can re-anchor inline
 * comments under the new section ids.
 */
function convertGuideToPattern(content: Content): ContentConversionResult {
  const targetType = ContentType.Pattern;
  const contentSubType = requireConversionSubType(targetType);
  const commentConversions: CommentConversion[] = [];

  const updated = deepCloneContent(content);

  convertContentTypeMetadata(updated.contentTypeMetadata, contentSubType);
  convertContentMetadata(updated.contentMetadata, targetType);
  convertTemplateMetadata(updated, contentSubType);

  // Build the legacy default page→section map: root content into the
  // `summary` section, every additional page into `miscellaneous`.
  const pageSectionMap = new Map<string, string>();
  for (const page of getContentPages(updated)) {
    pageSectionMap.set(
      page.id,
      page.id === ROOT_SECTION_ID ? ROOT_PAGE_DEFAULT_SECTION_ID : NON_ROOT_PAGE_DEFAULT_SECTION_ID,
    );
  }

  // Build the new Pattern's section nodes from the schema and concat each
  // Guide page's body (and its nested sections / sub-pages) into the
  // mapped section.
  const newContentSections = buildPatternSectionsFromSchema(contentSubType);
  collapseRootPageIntoMappedSection(
    updated.contentData,
    newContentSections,
    pageSectionMap,
    commentConversions,
  );
  collapseSubPagesIntoMappedSections(
    updated.contentData,
    newContentSections,
    pageSectionMap,
    commentConversions,
  );

  convertCodeRepository(updated, targetType);
  autoGenerateHref(updated.contentData, updated.contentMetadata, true);

  return { content: updated, commentConversions };
}

/** Builds the Pattern's `contentSections` by reading the destination schema. */
function buildPatternSectionsFromSchema(contentSubType: ContentSubType): ContentNode[] {
  const sectionSchema = getTemplateSchema(contentSubType, ContentSchemaSection.ContentSections);
  const sectionProps = sectionSchema.properties;
  return Object.keys(sectionProps).map((id) => {
    const { label, type } = sectionProps[id];

    // Structured sections (type "object" in the schema) are array-backed; everything else stores HTML.
    const initialContentData: unknown = type === 'object' ? [] : '';

    return Object.assign(new ContentNode(), {
      id,
      title: label,
      nodeType: ContentNodeType.Section,
      contentData: initialContentData,
    });
  });
}

/**
 * Collapses the Guide root page into its mapped section. The root's
 * direct `contentSections` (sub-sections rendered on the root page in
 * Guide) are appended below.
 */
function collapseRootPageIntoMappedSection(
  contentData: ContentNode,
  newContentSections: readonly ContentNode[],
  pageSectionMap: ReadonlyMap<string, string>,
  commentConversions: CommentConversion[],
): void {
  const mainSection = newContentSections.find((s) => s.id === pageSectionMap.get(ROOT_SECTION_ID));
  if (!mainSection) return;
  const concatData = concatContentData(
    (mainSection.contentData as string) ?? '',
    getH2Heading(getText(contentData.title)),
    contentData.contentData,
  );
  mainSection.contentData = getJSON(concatData);
  contentData.contentData = '';
  commentConversions.push(CommentConversion.create(ROOT_SECTION_ID, mainSection.id));
  for (const nested of contentData.contentSections) {
    appendNestedSectionToPatternSection(nested, mainSection, commentConversions);
  }
}

/**
 * Collapses every Guide sub-page into its mapped Pattern section, then
 * replaces `subContent` with `[]` and `contentSections` with the new
 * Pattern section list.
 */
function collapseSubPagesIntoMappedSections(
  contentData: ContentNode,
  newContentSections: ContentNode[],
  pageSectionMap: ReadonlyMap<string, string>,
  commentConversions: CommentConversion[],
): void {
  for (const page of contentData.subContent) {
    const target = newContentSections.find((s) => s.id === pageSectionMap.get(page.id));
    // The legacy converter trusts the map to always resolve; mirror that
    // and skip pages without a target rather than silently corrupting them.
    if (!target) continue;
    appendPageToPatternSection(page, target, commentConversions);
  }
  contentData.subContent = [];
  contentData.contentSections = newContentSections;
}

/** Appends a Guide page (heading + body, then its nested sections, then its sub-pages) onto a Pattern section. */
function appendPageToPatternSection(
  page: ContentNode,
  section: ContentNode,
  commentConversions: CommentConversion[],
): void {
  let contentData = (getHTML(section.contentData) as string) ?? '';
  if (contentData) contentData += getSeparator();
  const concatData = concatContentData(
    contentData,
    getH2Heading(getText(page.title)),
    page.contentData,
  );
  section.contentData = getJSON(concatData);
  commentConversions.push(CommentConversion.create(page.id, section.id));
  for (const nested of page.contentSections) {
    appendNestedSectionToPatternSection(nested, section, commentConversions);
  }
  for (const subPage of page.subContent) {
    appendPageToPatternSection(subPage, section, commentConversions);
  }
}

/** Appends a Guide nested section as `<H3>` + body onto a Pattern section. */
function appendNestedSectionToPatternSection(
  nested: ContentNode,
  section: ContentNode,
  commentConversions: CommentConversion[],
): void {
  const concatData = concatContentData(
    section.contentData,
    getH3Heading(getText(nested.title)),
    nested.contentData,
  );
  section.contentData = getJSON(concatData);
  commentConversions.push(CommentConversion.create(nested.id, section.id));
}
